import { useEffect, useReducer } from 'react';
import styled, { css } from 'styled-components';
import { Loader as Spinner } from '@elfsight/widget-ui';
import type {
  DirectionType,
  InteractionBehaviourType,
  LabelPositionType,
  PairType
} from '../../../types';
import { preloadImage } from '../../utils';
import { useFullscreen } from '../fullscreen';
import { PairSliderControlFullscreen } from './PairSliderControlFullscreen';
import { PairSliderDivider } from './PairSliderDivider';
import { PairSliderDivisionControl } from './PairSliderDivisionControl';
import { PairSliderLabel } from './PairSliderLabel';
import { PairSliderPicture } from './PairSliderPicture';

const IMAGE_PLACEHOLDER_BEFORE =
  'https://static.elfsight.com/icons/app-before-and-after-slider-image-placeholder-before-multicolor.svg';
const IMAGE_PLACEHOLDER_AFTER =
  'https://static.elfsight.com/icons/app-before-and-after-slider-image-placeholder-after-multicolor.svg';

const initialState = {
  isInteracting: false,
  isDragMoving: false,
  dividerPosition: 0.5,
  isLoaded: false
};

const reducer = (state = initialState, action) => {
  const { type: actionType, payload = null } = action;

  switch (actionType) {
    default:
      throw new Error(`Unknown action type "${actionType}"`);

    case 'SET_DIVIDER_POSITION':
      return {
        ...state,
        dividerPosition: payload
      };

    case 'START_INTERACTION':
      return {
        ...state,
        isInteracting: true
      };

    case 'STOP_INTERACTION':
      return {
        ...state,
        isInteracting: false
      };

    case 'START_DRAG_MOVE':
      return {
        ...state,
        isDragMoving: true
      };

    case 'STOP_DRAG_MOVE':
      return {
        ...state,
        isDragMoving: false
      };

    case 'START_LOADING':
      return {
        ...state,
        isLoaded: false
      };

    case 'STOP_LOADING':
      return {
        ...state,
        isLoaded: true
      };
  }
};

interface PairSliderProps {
  viewWidth: number;
  viewHeight: number;
  direction: DirectionType;
  dividerInitialPosition: number;
  dividerSize: number;
  dividerArrowsEnabled: boolean;
  dividerResetOnMouseOut: boolean;
  labelPosition: LabelPositionType;
  interactionBehaviour: InteractionBehaviourType;
  fullscreenEnabled: boolean;
  data: PairType;
  compact: boolean;
  isMobile: boolean;
  isSafari: boolean;
  labelFontSize: number;
  labelBackgroundColor?: string;
  dividerColor?: string;
}

export function PairSlider({
  viewWidth,
  viewHeight,
  direction,
  dividerInitialPosition,
  dividerSize,
  dividerArrowsEnabled,
  dividerResetOnMouseOut,
  labelPosition,
  interactionBehaviour,
  fullscreenEnabled,
  data,
  compact,
  isMobile,
  isSafari,
  labelFontSize,
  labelBackgroundColor,
  dividerColor,
  onLoaded
}: PairSliderProps) {
  const axis = direction === 'horizontal' ? 'x' : 'y';
  const isAxisX = axis === 'x';
  const isAxisY = axis === 'y';
  const isInteractionBehaviourHover = interactionBehaviour === 'hover';
  const isHoverInteractionEnabled = !isMobile && isInteractionBehaviourHover;
  const interactionCursorType = isAxisX ? 'ew-resize' : 'ns-resize';
  const labelDirectedPosition = `${direction}-${labelPosition}` as const;

  let fullscreenControlPosition: 'top-right' | 'bottom-left' | 'bottom-right';

  if (direction === 'horizontal' && labelPosition === 'start') {
    fullscreenControlPosition = 'bottom-right';
  } else if (direction === 'vertical' && labelPosition === 'end') {
    fullscreenControlPosition = 'bottom-left';
  } else {
    fullscreenControlPosition = 'top-right';
  }

  const { id, beforeImage, beforeImageLabel, afterImage, afterImageLabel } =
    data || {};

  const beforeImageUrl = beforeImage?.data?.url;

  const afterImageUrl = afterImage?.data?.url;

  const { open: openFullscreen } = useFullscreen();

  const [state, dispatch] = useReducer(reducer, initialState);
  const { isInteracting, isDragMoving, dividerPosition, isLoaded } = state;

  const isDividerMoving =
    (isInteractionBehaviourHover && isInteracting) || isDragMoving;

  const afterPictureClipPath = isAxisY
    ? `inset(${parseInt(dividerPosition * viewHeight, 10)}px 0 0 0)`
    : `inset(0 0 0 ${parseInt(dividerPosition * viewWidth, 10)}px)`;

  const smoothTransition =
    !isHoverInteractionEnabled &&
    !isDragMoving &&
    (dividerPosition === 0 || dividerPosition === 1);

  const setDividerPosition = (value) => {
    if (value !== dividerPosition) {
      dispatch({
        type: 'SET_DIVIDER_POSITION',
        payload: value
      });
    }
  };

  const startInteraction = () => {
    if (!isInteracting) {
      dispatch({ type: 'START_INTERACTION' });
    }
  };

  const finishInteraction = () => {
    if (isInteracting) {
      dispatch({ type: 'STOP_INTERACTION' });
    }
  };

  const startDragMove = () => {
    if (!isDragMoving) {
      dispatch({ type: 'START_DRAG_MOVE' });
    }
  };

  const stopDragMove = () => {
    if (isDragMoving) {
      dispatch({ type: 'STOP_DRAG_MOVE' });
    }
  };

  const startLoading = () => dispatch({ type: 'START_LOADING' });
  const stopLoading = () => {
    onLoaded?.();
    dispatch({ type: 'STOP_LOADING' });
  };

  const handleBeforeImageClick = () => setDividerPosition(1);
  const handleAfterImageClick = () => setDividerPosition(0);

  const handleFullscreenControlClick = () => openFullscreen(id);

  useEffect(() => {
    const imagesToLoad = [beforeImageUrl, afterImageUrl].filter(Boolean);

    if (imagesToLoad.length > 0) {
      const [cancel, loadingPromise] = preloadImage(...imagesToLoad);

      startLoading();
      loadingPromise.then(stopLoading);

      return cancel;
    }

    stopLoading();
    return undefined;
  }, [beforeImageUrl, afterImageUrl]);

  if (!isLoaded) {
    return (
      <Container
        _elementWidth={viewWidth}
        _elementHeight={viewHeight}
        _elementCursor={isInteracting ? interactionCursorType : undefined}
      >
        <Loader>
          <Spinner size={28} color={dividerColor} />
        </Loader>
      </Container>
    );
  }

  return (
    <Container
      _elementWidth={viewWidth}
      _elementHeight={viewHeight}
      _elementCursor={isInteracting ? interactionCursorType : undefined}
    >
      {beforeImageLabel && (
        <PairSliderLabel
          position={`before-${labelDirectedPosition}`}
          compact={compact}
          hidden={isDividerMoving}
          fontSize={labelFontSize}
          backgroundColor={labelBackgroundColor}
          onClick={handleBeforeImageClick}
        >
          {beforeImageLabel}
        </PairSliderLabel>
      )}

      {afterImageLabel && (
        <PairSliderLabel
          position={`after-${labelDirectedPosition}`}
          compact={compact}
          hidden={isDividerMoving}
          fontSize={labelFontSize}
          backgroundColor={labelBackgroundColor}
          onClick={handleAfterImageClick}
        >
          {afterImageLabel}
        </PairSliderLabel>
      )}

      {!isMobile && fullscreenEnabled && (
        <PairSliderControlFullscreen
          backgroundColor={labelBackgroundColor}
          position={fullscreenControlPosition}
          compact={compact}
          onClick={handleFullscreenControlClick}
        />
      )}

      <PairSliderPicture src={beforeImageUrl || IMAGE_PLACEHOLDER_BEFORE} />

      <PairSliderPicture
        src={afterImageUrl || IMAGE_PLACEHOLDER_AFTER}
        clipPath={afterPictureClipPath}
        smoothTransition={smoothTransition}
      />

      <PairSliderDivisionControl
        width={viewWidth}
        height={viewHeight}
        axis={axis}
        initialValue={dividerInitialPosition / 100}
        value={dividerPosition}
        simulateDragOnHover={isHoverInteractionEnabled}
        resetOnPointerOut={isHoverInteractionEnabled && dividerResetOnMouseOut}
        smoothTransition={smoothTransition}
        isMobile={isMobile}
        isSafari={isSafari}
        onChange={setDividerPosition}
        onUserInteractionStart={startInteraction}
        onUserInteractionEnd={finishInteraction}
        onDragMoveStart={startDragMove}
        onDragMoveEnd={stopDragMove}
      >
        <PairSliderDivider
          size={dividerSize}
          direction={direction}
          compact={compact}
          withArrows={dividerArrowsEnabled}
          color={dividerColor}
        />
      </PairSliderDivisionControl>
    </Container>
  );
}

const Container = styled.div<{
  _elementWidth: number;
  _elementHeight: number;
  _elementCursor?: string;
}>`
  position: relative;

  ${({
    _elementWidth: width,
    _elementHeight: height,
    _elementCursor: cursor
  }) =>
    css({
      width,
      height,
      cursor
    })};
`;

const Loader = styled.div`
  display: flex;
  position: absolute;
  inset: 0;
  justify-content: center;
  align-items: center;
`;
